// lib/types.ts

import { ExtendedUser } from "@/next-auth";
import {
  BloodType,
  Department,
  DocumentCategory,
  DocumentContentType,
  EmployeeStatus,
  Language,
  Profile,
} from "@prisma/client";

export type AlertType = "info" | "error" | "success" | "warning";
// lib/enums.ts

export enum EmploymentType {
  Permanent = "Permanent",
  Temporary = "Temporary",
}

export enum WorkLocation {
  InHouse = "In-House",
  Remote = "Remote",
  Hybrid = "Hybrid",
}

export enum WorkType {
  FullTime = "Full-Time",
  PartTime = "Part-Time",
  Contract = "Contract",
  Intern = "Intern",
}

export type Employee = {
  id: string;
  name: string;
  jobTitle: string;
  email?: string;
  employeeNo?: string;
  empStatus: EmployeeStatus;
  workLocation: WorkLocation;
  workType: WorkType;
  empType: EmploymentType;
};

export type Asset = {
  id: string;
  model: string;
  serialNumber: string;
  purchaseDate?: Date;
  price: string;
  notes: string;
  type: AssetType;
  status: AssetStatus;

  assignments: AssetAssignment[];
  history: AssetHistory[];
  attributes: AssetAttribute[];
};

export interface DocumentWithConsent {
  id: string;
  title: string;
  category: DocumentCategory;
  uploaderId: string;
  description: string | null;
  fileUrl: string | null; // null for a RICH_TEXT document (spec 0012)
  contentType: DocumentContentType;
  content: string | null;
  contentAr: string | null;
  groupId: string | null;
  createdAt: Date;
  updatedAt: Date;
  consentable: boolean;
  consentStatus: boolean; // Indicating if the user has consented
  consentedAt: Date | null; // Date of consent, if applicable
  signedName: string | null; // the persisted snapshot, or a Profile fallback for pre-spec-0012 rows
}

export type AssetAssignment = {
  id: string;
  assetId: string;
  userId: string;
  assignedOn: Date;
  returnedOn?: Date | null; // Optional field
};

export enum FormActionType {
  Add = "Add",
  Update = "Update",
}

// export type Department = {
//   name: string;
//   initials?: string;
//   href: string;
//   bgColor: string;
//   description?: string;
//   members?: number;
// };

export type AssetAttribute = {
  id: string;
  assetId: string;
  key: string;
  value: string;
  asset: Asset;
};

export type AssetHistory = {
  id: string;
  assetId: string;
  date: Date;
  eventType: AssetEventType;
  details?: string | null; // Optional field
  asset: Asset;
};

enum AssetEventType {
  CREATED = "CREATED",
  UPDATED = "UPDATED",
  MOVED = "MOVED",
  MAINTENANCE = "MAINTENANCE",
}

export enum AssetType {
  LAPTOP = "LAPTOP",
  PC = "PC",
  RAM = "RAM",
  FLASH_DRIVE = "FLASH_DRIVE",
  INT_HDD = "INT_HDD",
  EXT_HDD = "EXT_HDD",
  INT_SSD = "INT_SSD",
  EXT_SSD = "EXT_SSD",
  KEYBOARD = "KEYBOARD",
  MOUSE = "MOUSE",
  MOUSE_WIRELESS = "MOUSE_WIRELESS",
  MONITOR = "MONITOR",
  PRINTER = "PRINTER",
  SCANNER = "SCANNER",
  MULTIF_PRINTER = "MULTIF_PRINTER",
  ROUTER = "ROUTER",
  SWITCH = "SWITCH",
  IDPRINTER = "IDPRINTER",
  LAPTOP_BAG = "LAPTOP_BAG",
  AUDIO = "AUDIO",
  HUB = "HUB",
  OTHER = "OTHER",
}

export enum AssetStatus {
  AVAILABLE = "AVAILABLE",
  ASSIGNED = "ASSIGNED",
  UNDER_MAINTENANCE = "UNDER_MAINTENANCE",
  DECOMMISSIONED = "DECOMMISSIONED",
}

export type NavigationItemType = {
  name: string;
  href: string;
};

export enum LanguageType {
  EN = "en-US",
  AR = "ar-SA",
}

export enum ThemeType {
  LIGHT = "light",
  DARK = "dark",
}

export type Translation = {
  [key: string]: { [language in LanguageType]: string };
};

export interface Category {
  title: string;
  href: string;
}

export interface Author {
  name: string;
  role: string;
  href: string;
  imageUrl: string;
}

export interface Question {
  id: string;
  title: string;
  href: string;
  description: string;
  date: string;
  datetime: string;
  category: Category;
  author: Author;
}

export interface ConsentFormValues {
  employeeNo: string;
  fullName: string;
  
  email?: string; // Optional in case the user is not logged in
  documentId: string; // ID of the document being acknowledged
}

export type ExtendedUserWithProfile = ExtendedUser & {
  empNo?: string | null;
  profile: Profile & {
    arabicName: string;
  };
  accessToken: string; 
};

export interface UserWithProfile {
  id: string;
  name: string | null;
  email: string | null;
  profile: {
    userId: string | null;
    address: string | null;
    image: string | null;
    position: string | null;
    id: string;
    empNo: string | null;
    bloodType: BloodType | null;
    department: Department | null;
    fullName: string | null;
    mobile: string | null;
    language: Language | null;
  }[];
}

// app/lib/constants.ts

export const LANGUAGES = {
  EN: "en-US",
  AR: "ar-SA",
};

export const THEME = {
  LIGHT: "light",
  DARK: "dark",
};

export type ItemType = {
  id: string;
  name: string;
};

export interface UserTypeWithConsents {
  id: string;
  email: string;
  name: string;
  role: string;
  image: string;
  profile: Profile | null;
  userConsents: {
    document: Document;
  }[];
}

export interface UserProfile {
  id: string;
  image: string | null;
  empNo: string;
  name: string;
  arabicName: string;
  email: string;
  role: string;
  department: string;
  status: string;
  address: string | null;
  position: string | null;
  arabicPosition: string | null;
  bloodType: BloodType | null;
  fullName: string | null;
  mobile: string | null;
  userId: string | null;
  hasVcard: boolean | false;
  language: "en_US" | "ar_SA";
  // The real Profile.id (distinct from `id` above, which is the User.id)
  // -- needed anywhere a feature keys off Profile, e.g. asset assignments
  // (AssetAssignment.profileId), not User.
  profileId_en?: string | null;
  profileId_ar?: string | null;
}

export const statusBadgeMap: Record<
  EmployeeStatus,
  {
    label: string;
    variant: "success" | "secondary" | "destructive" | "pending" | "default" ;
  }
> = {
  [EmployeeStatus.ACTIVE]: { label: "Active", variant: "success" },
  [EmployeeStatus.VACATION]: { label: "On Leave", variant: "pending" }, // changed
  [EmployeeStatus.PROBATION]: { label: "Probation", variant: "default" },
  [EmployeeStatus.LEFT]: { label: "Left", variant: "destructive" },
};

export type VCardResponse = {
  empNo: string;

  qrCode?: string | null;
  wechatBarcode?: string | null;

  whatsApp?: string | null;

  image?: string | null;

  fullName?: string | null;
  fullNameAr?: string | null;

  position?: string | null;
  positionAr?: string | null;

  department?: string | null;
  departmentAr?: string | null;

  mobile?: string | null;
  email?: string | null;

  slug?: string | null;
};

export type IDCardData = {
  id: string;

  fullName: string;
  fullNameAr: string;

  position: string;
  positionAr: string;

  department?: Department;

  // Every current writer already falls back to null when a profile has
  // none set (lib/idcard/data.ts, both branches of GET /api/id-card); the
  // admin "view as" preview (spec 0007) needs that to actually be typed,
  // rather than relying on an `as IDCardData` cast to paper over it.
  bloodType: BloodType | null;
  email: string;

  empNo: string;
  imageUrl: string;
  residenceIdNo: string;

 

  user: {
    id: string;
  };
};

// export enum Department {
//   HUMAN_RESOURCES = "HUMAN_RESOURCES",
//   IT = "IT",
//   MARKETING = "MARKETING",
//   SALES = "SALES",
//   FINANCE = "FINANCE",
//   OPERATIONS = "OPERATIONS",
//   PRODUCT_DEVELOPMENT = "PRODUCT_DEVELOPMENT",
//   CUSTOMER_SERVICE = "CUSTOMER_SERVICE",
//   LEGAL = "LEGAL",
//   PROCUREMENT = "PROCUREMENT",
//   RESEARCH_AND_DEVELOPMENT = "RESEARCH_AND_DEVELOPMENT",
//   QUALITY_ASSURANCE = "QUALITY_ASSURANCE",
//   STRATEGY_AND_PLANNING = "STRATEGY_AND_PLANNING",
//   ENGINEERING = "ENGINEERING",
//   ADMINISTRATION = "ADMINISTRATION",
//   SECURITY = "SECURITY",
//   FACILITIES = "FACILITIES",
//   LOGISTICS = "LOGISTICS",
//   TRAINING = "TRAINING",
//   PRODUCTION = "PRODUCTION",
//   STOCK_CONTROL = "STOCK_CONTROL",
//   SUPPLY_CHAIN = "SUPPLY_CHAIN",
//   WAREHOUSING = "WAREHOUSING",
//   INVENTORY_MANAGEMENT = "INVENTORY_MANAGEMENT",
//   MANUFACTURING = "MANUFACTURING",
//   MAINTENANCE = "MAINTENANCE",
//   PROJECT_MANAGEMENT = "PROJECT_MANAGEMENT",
//   ENVIRONMENTAL_HEALTH_AND_SAFETY = "ENVIRONMENTAL_HEALTH_AND_SAFETY",
//   REGULATORY_COMPLIANCE = "REGULATORY_COMPLIANCE",
//   BUSINESS_DEVELOPMENT = "BUSINESS_DEVELOPMENT",
//   CORPORATE_COMMUNICATIONS = "CORPORATE_COMMUNICATIONS",
//   CORPORATE_SOCIAL_RESPONSIBILITY = "CORPORATE_SOCIAL_RESPONSIBILITY",
//   CUSTOMER_SUCCESS = "CUSTOMER_SUCCESS",
//   BUSINESS_ANALYTICS = "BUSINESS_ANALYTICS",
//   DATA_SCIENCE = "DATA_SCIENCE",
//   PROCUREMENT_PLANNING = "PROCUREMENT_PLANNING",
//   SHIPPING_AND_RECEIVING = "SHIPPING_AND_RECEIVING",
// }

// export const BloodTypeLabels = {
//   A_POS: "A+",
//   A_NEG: "A-",
//   B_POS: "B+",
//   B_NEG: "B-",
//   AB_POS: "AB+",
//   AB_NEG: "AB-",
//   O_POS: "O+",
//   O_NEG: "O-",
// } as const;

// export type BloodType = keyof typeof BloodTypeLabels;

export interface DesignData {
  name: string;
  job: string;
  email: string;
  mobile: string;
  disclaimer?: boolean; // Optional, defaults to true
  saudiMade?: boolean; // Optional, defaults to true
  saudiMadeOrientation?: "horizontal" | "vertical"; // Optional, defaults to "horizontal"
  rtl?: boolean; // Optional, defaults to true
}

export interface ProductsResponse {
  products: Product[];
  total: number;
  total_pages: number;
}

export interface Product {
  id: number;
  title: string;
  content: string;
  slug: string;
  link: string;
  excerpt: string;
  modified_gmt: string;
  featured: string;
  thumbnail: string;
  featured_image_url: string;
  product_category: ProductCategory | null;
  // Every category this product is tagged with (not just the first) --
  // needed to filter "show me everything under category X", since a
  // product can carry several tags at once.
  categoryIds: number[];
  acf: ProductACF;
}

export interface ProductCategory {
  id: number;
  link: string;
  name: string;
  product_count: number;
  subcategory_count: number;
  slug: string;
  acf: any;
  parentId: number | null;
  children: ProductCategory[];
  // Real per category photo (WPGraphQL productCategoryTaxonomyTerm.termThumbnail
  // / .termPhoto -- the same fields lib/data/productCategoryThumbnails.ts
  // already confirmed live), null when the category has none set.
  thumbnail: string | null;
}

export interface ProductACF {
  catalogues: Catalogue[];
}

export interface Catalogue {
  id: number;
  date: string;
  date_gmt: string;
  guid: Rendered;
  modified: string;
  modified_gmt: string;
  slug: string;
  link: string;
  title: string;
  featured_media: number;
  acf: CatalogueACF;
}

export interface Rendered {
  rendered: string;
}

export interface CatalogueACF {
  catalogue_thumbnail: number;
  catalogue_file: CatalogueFile;
  related_product_category: RelatedProductCategory[];
}

export interface CatalogueFile {
  id: number;
  title: string;
  filename: string;
  filesize: number;
  link: string;
  author: string;
  modified: string;
  menu_order: number;
}

export interface RelatedProductCategory {
  term_id: number;
  name: string;
  slug: string;
}


// Type for a single link within a dropdown
export interface SubMenuItem {
  name: string;
  href: string;
}

// A menu item that is a direct link
interface MenuLink {
  name: string;
  href: string;
  subItems?: never; // Ensures this property doesn't exist
}

// A menu item that is a dropdown
interface MenuDropdown {
  name: string;
  subItems: SubMenuItem[];
  href?: never; // Ensures this property doesn't exist
}

// The final type is a union of the two possibilities
export type MenuItem = MenuLink | MenuDropdown;



/* CERTIFICATES */

type MediaItem = {
  node: {
    mediaItemUrl: string;
  };
} | null;

export interface CertRelatedProduct {
  databaseId: number;
  title: string;
  slug: string;
  link: string;
  featuredImage?: { node?: { sourceUrl?: string | null } } | null;
}

export interface CertProductCategory {
  name: string;
  slug: string;
  link?: string | null;
}

export interface Certificate {
  title: string;
  arabicTitle: string;
  slug: string;
  link: string | null;
  status: string;
  featuredImage: MediaItem;
  certificatesOption: {
    companyLogo: MediaItem;
    gallery: { nodes: { mediaItemUrl: string }[] } | null;
    expiryDate: string | null;
    certificateFile: MediaItem;
    certRelatedProducts?: { nodes: CertRelatedProduct[] } | null;
    certProductCategories?: { nodes: CertProductCategory[] } | null;
  };
  terms: {
    nodes: TermNode[]; // Use the updated TermNode interface
  };
}

interface TermNode {
  name: string;
  translations:
    | {
        name: string;
      }[]
    | null;
}

// ─── Sales networks / branches / staff (spec 0003) ───────────────────────
// Sourced live from WordPress GraphQL (the real sales-network CPT + region
// taxonomy + an ACF "branches" repeater with a nested "staff" repeater), no
// local database table. See lib/data/branches.ts for the language
// resolution. Two levels only: a network holds branches, a branch holds
// staff — there is no deeper nesting and no separate "unassigned" bucket,
// since staff are embedded fields on a branch, never a separate entity.

export interface BranchStaffDisplay {
  /** WordPress media-library id of the photo (null when none); used as the expected value when changing it. */
  photoId?: number | null;
  /** Row number inside the WordPress ACF `staff` repeater; display order is re-sorted, so writes must use this. */
  wpIndex?: number;
  name: string;
  position: string;
  mobile: string;
  email: string;
  photoUrl: string | null;
}

export interface BranchLocation {
  // "{networkDatabaseId}-{branchIndex}" -- see lib/data/branchOverrides.ts.
  // Set once the live WordPress list passes through applyBranchOverrides();
  // absent on the raw getBranches() result before that.
  branchKey?: string;
  isHidden?: boolean;
  name: string;
  address: string;
  phone: string;
  fax: string;
  warehouse: string;
  mapUrl: string;
  // Raw coordinates for an interactive map marker (mapUrl above is a plain
  // "open in Google Maps" link, not enough to place a pin ourselves). Null
  // when WordPress's gmap field was never set for this branch.
  lat: number | null;
  lng: number | null;
  // The branch's own photo (a building/storefront shot), distinct from any
  // staff member's photo -- previously the map popup borrowed a staff
  // photo as a stand-in since this didn't exist yet.
  imageUrl: string | null;
  /** WordPress media-library id of the branch photo (null when none). */
  imageId?: number | null;
  staff: BranchStaffDisplay[];
}

export interface SalesNetworkDisplay {
  id: number;
  name: string;
  regionTags: string[];
  // Stable (language independent) id of the network's first region, used
  // for grouping/sorting; null for the handful of administrative entries
  // (e.g. "Government Sales") that carry no region at all.
  primaryRegionId: number | null;
  branches: BranchLocation[];
}

export interface BranchesData {
  networks: SalesNetworkDisplay[];
}

export interface BoardMemberDisplay {
  id: number;
  name: string;
  jobTitle: string;
  bioHtml: string;
  photoUrl: string | null;
}

// ─── In-app photo gallery (spec 0011) ────────────────────────────────────
export interface GalleryPhoto {
  url: string;
  alt: string;
}

export interface GalleryAlbum {
  id: number;
  title: string;
  coverUrl: string;
  photos: GalleryPhoto[];
}

export interface GalleryData {
  albums: GalleryAlbum[];
}

// ─── In-app News / blog posts (live from WordPress, same pattern as
// Branches and Gallery: no local copy, near-live via a 60s cache bustable
// by a revalidate webhook) ────────────────────────────────────────────────
export interface NewsPost {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  coverUrl: string | null;
  // Real WordPress category, when the post has one set (confirmed a
  // standard WPGraphQL field, not custom). A post with none (this
  // WordPress site's own Trainings section already shows
  // "Non-categorized" on several real items) simply has null here --
  // never a fabricated placeholder.
  category: string | null;
}

export interface NewsData {
  posts: NewsPost[];
}

// ─── Business card generator (spec 0002) ─────────────────────────────────
// No photo, blood type, or employee number, unlike IDCardData; a card
// render is always single language (see lib/businesscard/render.ts).
export interface BusinessCardData {
  id: string;
  fullName: string;
  fullNameAr: string;
  position: string;
  positionAr: string;
  department?: Department;
  mobile: string;
  email: string;
  // Same slug the digital VCard link (lib/vcard/generate-qr.ts) uses; the
  // card's own QR code points at that existing page rather than a new one.
  slug: string | null;
}